// templateParser.ts
import type { IShopifyPageAssigned, IShopifyPageAssignedResponse } from '~/modules/shopify/types';
import type { IThemePage } from '~/stores/themePage';
import type { TemplateInfo } from '~/types/template';

import { parseProviderSources } from '~/constants/providerSource';
import { TemplateType, ProviderSource } from '~/types/template';
import { getProviderSourceParser } from './templateHelper';

interface IParseTemplateInfoOptions {
  templateSuffix?: string;
  themePages?: IThemePage[];
}
/**
 * @param {IParseTemplateInfoOptions} options - Options
 * @returns {TemplateInfo | undefined} Template info
 * @example
 * const themePages = [
 *   {
 *     id: '123',
 *     type: 'product',
 *     default: true,
 *   },
 * ];
 * const templateInfo = parseTemplateInfoBy({
 *   templateSuffix: 'templates/product.json',
 *   themePages: themePages,
 * });
 * console.log(templateInfo);
 * // {
 * //   ...
 * //   providerSource: ProviderSource.GEMPAGESV7,
 * //   suffix: 'gp-template-123',
 * // }
 **/
export const parseTemplateInfoBy = (
  options: IParseTemplateInfoOptions,
): TemplateInfo | undefined => {
  const { templateSuffix, themePages } = options;
  if (!templateSuffix) return;

  let templateInfo: TemplateInfo | undefined = undefined;
  for (const source of parseProviderSources) {
    templateInfo = source.parseTemplate({
      assetKey: templateSuffix,
      providerSourceInfo: source,
    });
    if (templateInfo) break;
  }

  // TODO: Check logic
  if (themePages?.length && templateInfo?.providerSource === ProviderSource.SHOPIFY_ORIGIN) {
    const themePageByDefault = themePages?.find(
      (page) => !!page.default && page.type === templateInfo.type,
    );
    if (themePageByDefault) {
      return {
        ...templateInfo,
        providerSource: ProviderSource.GEMPAGESV7,
        suffix: `gp-template-${themePageByDefault.id}`,
      };
    }
  }

  return templateInfo;
};

export const parseTemplateType = (templateType?: TemplateType): string => {
  switch (templateType) {
    case TemplateType.PAGE:
      return 'Landing page';
    case TemplateType.COLLECTION:
      return 'Collection page';
    case TemplateType.PRODUCT:
      return 'Product page';
    case TemplateType.ARTICLE:
      return 'Blog post page';
    case TemplateType.HOME:
      return 'Home page';
    case TemplateType.BLOG:
      return 'Blog page';
    case TemplateType.CART:
      return 'Cart page';
    case TemplateType.SEARCH:
      return 'Search page';
    case TemplateType.NOT_FOUND:
      return '404 page';
    case TemplateType.PASSWORD:
      return 'Password page';
    case TemplateType.GIF_CART:
      return 'GIF Cart page';
    default:
      return 'Unknown';
  }
};

export const parseTemplateIdBy = (
  assetKey: string,
  providerSource: ProviderSource,
): string | undefined => {
  const source = getProviderSourceParser(providerSource);
  if (!source?.regexId) return;

  const match = source.regexId?.exec(assetKey);
  if (match?.length && match[1]) {
    return match[1];
  }
};

export const parseAssignedTemplates = ({
  shopURL,
  assetKey,
  templateAssigned,
}: {
  shopURL: string;
  assetKey: string;
  templateAssigned?: IShopifyPageAssignedResponse;
}): IShopifyPageAssigned | undefined => {
  const templateSuffix = parseTemplateInfoBy({ templateSuffix: assetKey })?.suffix ?? '';
  switch (templateSuffix) {
    case TemplateType.HOME:
    case TemplateType.CART:
      return getAssignedDefaultByType(shopURL);
  }

  return templateAssigned?.[templateSuffix];
};

export const getAssignedDefaultByType = (shopURL: string) => {
  return {
    url: shopURL,
    total: 1,
    data: [],
  };
};
